import { useEffect, useState } from "react";
import { useDonationCampaign, markDonationIntent } from "./DonateButton";
import { HeartHandshake, ArrowUpRight } from "lucide-react";

const JG_SCRIPT = "https://www.justgiving.com/widgets/scripts/widget.js";

/** Official JustGiving Giving Checkout embed. Loads JustGiving's widget script
 * only on this page and only when the charity's widget identifiers (charityId
 * or linkId from their JustGiving account) are configured. Falls back to a
 * normal JustGiving page link, then to the honest setup state. */
export default function JustGivingCheckout() {
  const cfg = useDonationCampaign();
  const [scriptFailed, setScriptFailed] = useState(false);
  const hasWidget = Boolean(cfg.justgiving_charity_id || cfg.justgiving_link_id);

  useEffect(() => {
    if (!hasWidget) return;
    const script = document.createElement("script");
    script.src = JG_SCRIPT;
    script.async = true;
    script.type = "text/javascript";
    script.dataset.version = "2.0";
    script.dataset.widgetType = "donateButton";
    script.dataset.linkType = "givingCheckout";
    script.dataset.donateButtonType = "justgivingSmall";
    script.dataset.marketCode = "GB";
    script.dataset.showPaymentLogos = "true";
    script.dataset.popupCheckout = "true";
    if (cfg.justgiving_charity_id) script.dataset.charityId = cfg.justgiving_charity_id;
    if (cfg.justgiving_link_id) script.dataset.linkId = cfg.justgiving_link_id;
    script.onerror = () => setScriptFailed(true);
    document.body.appendChild(script);
    return () => script.remove();
  }, [hasWidget, cfg.justgiving_charity_id, cfg.justgiving_link_id]);

  // Widget active: JustGiving's script enhances this button into the checkout popup.
  if (hasWidget && !scriptFailed) {
    return (
      <div data-testid="justgiving-widget-slot">
        <button
          type="button"
          data-jg-donate-button="true"
          data-marketCode="GB"
          data-linkType="givingCheckout"
          {...(cfg.justgiving_charity_id ? { "data-charityId": cfg.justgiving_charity_id } : {})}
          {...(cfg.justgiving_link_id ? { "data-linkId": cfg.justgiving_link_id } : {})}
          aria-label="Donate with JustGiving"
          data-testid="justgiving-checkout-button"
          className="inline-flex items-center gap-2 bg-gold text-white font-medium px-8 py-3.5 hover:bg-gold-dark transition-colors duration-300"
        >
          <HeartHandshake size={18} aria-hidden="true" />
          Donate with JustGiving
        </button>
        <p className="mt-2 text-xs text-charcoal/60 italic">
          Secure checkout opens in JustGiving's official popup — you never leave this site.
        </p>
      </div>
    );
  }

  // Fallback: page URL configured but no widget IDs (or script blocked) → normal link.
  if (cfg.campaign_configured && cfg.campaign_url) {
    return (
      <div data-testid="justgiving-link-fallback">
        <a
          href={cfg.campaign_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={markDonationIntent}
          data-testid="justgiving-link-button"
          className="inline-flex items-center gap-2 bg-gold text-white font-medium px-8 py-3.5 hover:bg-gold-dark transition-colors duration-300"
        >
          <HeartHandshake size={18} aria-hidden="true" />
          Donate via JustGiving <ArrowUpRight size={15} aria-hidden="true" />
        </a>
        {scriptFailed && (
          <p role="status" className="mt-2 text-xs text-charcoal/60 italic">
            The embedded checkout is unavailable right now — the button above opens our secure JustGiving page instead.
          </p>
        )}
      </div>
    );
  }

  // Nothing configured yet: honest setup state.
  return (
    <div data-testid="justgiving-setup-state">
      <button
        type="button"
        disabled
        aria-disabled="true"
        className="inline-flex items-center gap-2 bg-gold/60 text-white font-medium px-8 py-3.5 cursor-not-allowed"
      >
        <HeartHandshake size={18} aria-hidden="true" />
        Donate via JustGiving
      </button>
      <p className="mt-2 text-xs text-charcoal/60 italic">
        Our official JustGiving page is being set up — this button will go live once it is published.
      </p>
    </div>
  );
}
